import moment from 'moment/moment'
import { type PropsWithChildren, useMemo, useEffect, useRef } from 'react'
import { Provider } from 'react-redux'
import { routes } from '@/routes'
import { issueMessages } from '@sevenrooms/form'
import { CurrencyLocale, Locale } from '@sevenrooms/locales'
import { Route, Router, Switch } from '@sevenrooms/navigation'
import { messages as shift4Messages, saferPayMessages, networkInternationalMessages } from '@sevenrooms/payments'
import { TimeLocale } from '@sevenrooms/timepiece'
import { facebookButtonMessages, promoCodeMessages, ReCaptcha, type ReCAPTCHARef } from '@sevenrooms/ui-kit/core'
import { formTimeRangePickerMessages } from '@sevenrooms/ui-kit/form'
import { Root, logoFooterMessages } from '@sevenrooms/ui-kit/layout'
import '@sevenrooms/ui-kit/fonts/Inter.css'
import '@sevenrooms/ui-kit/fonts/Roboto.css'
import { AppMeta } from './AppMeta'
import {
  guestPreferencesMessages,
  reservationDetailsMessages,
  reservationAvailabilityMessages,
  priorityAlertDetailsMessages,
  venueDetailsMessages,
  ThirdPartyLoginInitializer,
} from './components'
import { ReCaptchaContext } from './contexts'
import { useVenue, useLanguageStrings, useWidgetSettings, useWidgetLanguage, useAnalyticsSettings, useFavicon } from './hooks'
import { Reservations } from './Reservations'
import { additionalUpgradesCardMessages } from './Reservations/Manage/AdditionalUpgradesCard'
import { paymentDetailsMessages } from './Reservations/Manage/PaymentDetails'
import { paymentHistoryMessages } from './Reservations/Manage/PaymentDetails/PaymentHistory'
import { policyDetailsMessages } from './Reservations/Manage/PolicyDetails'
import { upgradeDetailsMessages } from './Reservations/Manage/UpgradeDetails'
import { upgradesBannerMessages } from './Reservations/Manage/UpgradesBanner'
import { reservationAvailabilityModalMessages } from './Reservations/Search/ReservationAvailabilityModal'
import { store } from './store'
import { widgetLoadStarted } from './utils'
import { configureRecaptcha } from './utils/configureRecaptcha'
import { VenueDataLoader } from './VenueDataLoader'

function AppRoot({ children }: PropsWithChildren<{}>) {
  useFavicon()
  const { locale, venueColor, urlKey: venueUrlKey } = useVenue()
  const widgetSettings = useWidgetSettings()
  const { selectedLanguage } = useWidgetLanguage()
  const {
    analyticsEventMapping: { pageView },
    fbPixelIdsAll,
  } = useAnalyticsSettings()
  const venueLocale = Locale.getLocale(locale)
  const venueSensitiveLocale = Locale.getVenueSensitiveLocale(venueLocale, selectedLanguage)
  const { messages: widgetMessages } = useLanguageStrings()

  useEffect(() => {
    CurrencyLocale.setLocale(venueLocale)
    TimeLocale.setLocale(venueSensitiveLocale)
    TimeLocale.setDefaultFormatOptions({ hour12: venueLocale?.toLowerCase() !== 'en-gb' })
    moment.locale(venueSensitiveLocale)
    widgetLoadStarted(venueUrlKey, fbPixelIdsAll, pageView)
  }, [venueSensitiveLocale, fbPixelIdsAll, venueLocale, venueUrlKey, pageView])

  const messages = useMemo(() => {
    if (!widgetMessages) {
      return undefined
    }
    return {
      ...widgetMessages,
      [paymentDetailsMessages.title.id]: widgetMessages.commonPaymentDetailsText,
      [paymentDetailsMessages.total.id]: widgetMessages.commonTotalText,
      [upgradeDetailsMessages.title.id]: widgetMessages.commonUpgradesLabel,
      [venueDetailsMessages.getDirections.id]: widgetMessages.commonGetDirectionsLink,
      [policyDetailsMessages.bookingCheckoutPolicyHeader.id]: widgetMessages.resConfEmailHeader,
      [policyDetailsMessages.specialAttentionMessageHeader.id]: widgetMessages.resWidgetSpecialAttentionLabel,
      [policyDetailsMessages.specialAttentionMessageBody.id]: widgetMessages.resWidgetSpecialAttentionInfoBody,
      [policyDetailsMessages.cancellationPolicyHeader.id]: widgetMessages.commonCancellationPolicyHeader,
      [reservationDetailsMessages.partySize.id]: widgetMessages.resCancelPagePartySize,
      [reservationDetailsMessages.tableMinimum.id]: widgetMessages.resCancelPageTableMinText,
      [reservationDetailsMessages.reservedFor.id]: widgetMessages.commonReservedForText,
      [reservationDetailsMessages.reservationNumber.id]: widgetMessages.resCancelPageReservationId,
      [guestPreferencesMessages.guestPreferencesSave.id]: widgetMessages.resWidgetSaveButtonLabel,
      [guestPreferencesMessages.guestDietaryRestriction.id]: widgetMessages.resConfirmPageDietaryRestrictQuestion,
      [guestPreferencesMessages.guestImageHeader.id]: widgetMessages.resConfirmPageProfilePhotoDesc,
      [guestPreferencesMessages.guestImageUploadButton.id]: widgetMessages.resConfirmPageUploadPhotoButton,
      [guestPreferencesMessages.guestImageDeleteButton.id]: widgetMessages.resConfirmPageRemovePhotoButton,
      [guestPreferencesMessages.guestOtherInfo.id]: widgetMessages.resConfirmPageOtherInfoLabel,
      [guestPreferencesMessages.guestPreferencesHeader.id]: widgetMessages.resConfirmPageDesc,
      [guestPreferencesMessages.guestSpecialOccasion.id]: widgetMessages.resConfirmPageSpecialOccLabel,
      [guestPreferencesMessages.partyDietaryRestriction.id]: widgetMessages.resConfirmPageDietaryRestrictGroupQuestion,
      [guestPreferencesMessages.guestChampagnePreference.id]: widgetMessages.resConfirmPageSearchChampagnePrefLabel,
      [guestPreferencesMessages.guestLiquorPreference.id]: widgetMessages.resConfirmPageLiquorPref,
      [guestPreferencesMessages.guestPreferencesCancel.id]: widgetMessages.resConfirmPageCancelButton,
      [guestPreferencesMessages.guestImageEditButton.id]: widgetMessages.resConfirmPageEditPhoto,
      [guestPreferencesMessages.guestImageReUploadButton.id]: widgetMessages.resConfirmPageReuploadPhoto,
      [paymentHistoryMessages.title.id]: widgetMessages.commonHistoricalTransactionsText,
      [paymentHistoryMessages.chargedText.id]: widgetMessages.commonChargedText,
      [paymentHistoryMessages.refundedText.id]: widgetMessages.commonRefundedText,
      [additionalUpgradesCardMessages.availableUpgradesTitle.id]: widgetMessages.resConfirmPageAvailableUpgradesSubheader,
      [additionalUpgradesCardMessages.availableUpgradesShowMoreTitle.id]: widgetMessages.resConfirmPageViewMore,
      [additionalUpgradesCardMessages.availableUpgradesShowLessTitle.id]: widgetMessages.resConfirmPageViewLess,
      [additionalUpgradesCardMessages.availableUpgradesShowAllTitle.id]: widgetMessages.resConfirmPageViewAllUpgrades,
      [upgradesBannerMessages.title.id]: widgetMessages.resConfirmPageAvailableUpgradesSubheader,
      [upgradesBannerMessages.viewUpgradesButton.id]: widgetMessages.resConfirmPageAvailableUpgradesButton,
      [logoFooterMessages.footerLabel.id]: widgetMessages.resWidgetPoweredByLabel,
      [priorityAlertDetailsMessages.header.id]: widgetMessages.resCancelPageCancelPriorityAlertsSubheader,
      [priorityAlertDetailsMessages.partySize.id]: widgetMessages.resCancelPagePartySize,
      [reservationAvailabilityModalMessages.perPerson.id]: widgetMessages.resWidgetPerPersonLabel,
      [reservationAvailabilityModalMessages.perReservation.id]: widgetMessages.resWidgetPerReservationLabel,
      [reservationAvailabilityModalMessages.confirmButton.id]: widgetMessages.resWidgetSelectButtonLabel,
      [reservationAvailabilityModalMessages.cancellationPolicyHeader.id]: widgetMessages.commonCancellationPolicyHeader,
      [reservationAvailabilityModalMessages.upgradesTitle.id]: widgetMessages.reservationTimeslotIncludedInReservationLabel,
      [reservationAvailabilityModalMessages.descriptionTitle.id]: widgetMessages.reservationTimeslotAboutLabel,
      [reservationAvailabilityMessages.resWidgetPerPersonLabel.id]: widgetMessages.resWidgetPerPersonLabel,
      [reservationAvailabilityMessages.resWidgetPerReservationLabel.id]: widgetMessages.resWidgetPerReservationLabel,
      [facebookButtonMessages.text.id]: widgetMessages.resWidgetFacebookCheckoutButton,
      [promoCodeMessages.promoButtonApplyLabel.id]: widgetMessages.resWidgetPromoButtonApplyLabel,
      [promoCodeMessages.promoButtonRemoveLabel.id]: widgetMessages.resWidgetPromoButtonRemoveLabel,
      [promoCodeMessages.promoInputLabel.id]: widgetMessages.resWidgetPromoLabel,
      [promoCodeMessages.promoInputPlaceholder.id]: widgetMessages.resWidgetPromoPlaceholder,
      [promoCodeMessages.promoInputAppliedStatusLabel.id]: widgetMessages.resWidgetPromoAppliedStatusLabel,
      [issueMessages.required.id]: widgetMessages.resWidgetErrorsFieldRequired,
      [issueMessages.invalid.id]: widgetMessages.resWidgetErrorsFieldInvalid,
      [formTimeRangePickerMessages.chooseStartTime.id]: widgetMessages.resWidgetChooseStartTime,
      [formTimeRangePickerMessages.chooseEndTime.id]: widgetMessages.resWidgetChooseEndTime,
      [formTimeRangePickerMessages.errorGreaterThanEnd.id]: widgetMessages.resWidgetTimerangeErrorStartBeforeEnd,
      [formTimeRangePickerMessages.errorSameTime.id]: widgetMessages.resWidgetTimerangeErrorStartEndNotSame,
      [shift4Messages.paymentLabel.id]: widgetMessages.resWidgetPaymentLabel,
      [shift4Messages.creditCardCardNumLabel.id]: widgetMessages.resCreditCardCardNumLabel,
      [shift4Messages.creditCardCardNumPlaceholder.id]: widgetMessages.resCreditCardCardNumLabel,
      [shift4Messages.creditCardCardNumError.id]: widgetMessages.resWidgetErrorsCardIncomplete,
      [shift4Messages.creditCardExpDateLabel.id]: widgetMessages.creditCardExpDateLabel,
      [shift4Messages.creditCardExpDatePlaceholder.id]: widgetMessages.creditCardExpDatePlaceholder,
      [shift4Messages.creditCardExpDateError.id]: widgetMessages.resWidgetErrorsCardExpired,
      [shift4Messages.creditCardSecurityCodeLabel.id]: widgetMessages.creditCardSecurityCodeLabel,
      [shift4Messages.creditCardSecurityCodePlaceholder.id]: widgetMessages.resCreditCardCvvLabel,
      [shift4Messages.creditCardSecurityCodeError.id]: widgetMessages.commonPaymentErrorIncompleteSecCode,
      [shift4Messages.billingAddressLabel.id]: widgetMessages.billingAddressLabel,
      [shift4Messages.billingAddressPlaceholder.id]: widgetMessages.billingAddressEnterYourAddressLabel,
      [shift4Messages.billingAddressError.id]: widgetMessages.resWidgetErrorsFieldRequired,
      [shift4Messages.postalCodeLabel.id]: widgetMessages.resWidgetPostalCodeLabel,
      [shift4Messages.postalCodePlaceholder.id]: widgetMessages.resWidgetPostalCodeLabel,
      [shift4Messages.postalCodeError.id]: widgetMessages.commonPaymentErrorIncompletePostalCode,
      [saferPayMessages.paymentLabel.id]: widgetMessages.resWidgetPaymentLabel,
      [saferPayMessages.creditCardHolderNameLabel.id]: widgetMessages.resCreditCardHolderName,
      [saferPayMessages.creditCardHolderNamePlaceholder.id]: widgetMessages.resCreditCardHolderName,
      [saferPayMessages.creditCardHolderError.id]: widgetMessages.resWidgetErrorsFieldRequired,
      [saferPayMessages.creditCardCardNumLabel.id]: widgetMessages.resCreditCardCardNumLabel,
      [saferPayMessages.creditCardCardNumPlaceholder.id]: widgetMessages.resCreditCardCardNumLabel,
      [saferPayMessages.creditCardCardNumError.id]: widgetMessages.resWidgetErrorsCardIncomplete,
      [saferPayMessages.creditCardExpDateLabel.id]: widgetMessages.creditCardExpDateLabel,
      [saferPayMessages.creditCardExpDatePlaceholder.id]: widgetMessages.creditCardExpDatePlaceholder,
      [saferPayMessages.creditCardExpDateError.id]: widgetMessages.resWidgetErrorsFieldRequired,
      [saferPayMessages.creditCardSecurityCodeLabel.id]: widgetMessages.creditCardSecurityCodeLabel,
      [saferPayMessages.creditCardSecurityCodePlaceholder.id]: widgetMessages.resCreditCardCvvLabel,
      [saferPayMessages.creditCardSecurityCodeError.id]: widgetMessages.commonPaymentErrorIncompleteSecCode,
      [networkInternationalMessages.paymentLabel.id]: widgetMessages.resWidgetPaymentLabel,
    }
  }, [widgetMessages])

  const brandColor = widgetSettings.colorPrimary

  const themeOverride = useMemo(
    () => ({
      colors: {
        brandColor,
        venueColor,
      },
    }),
    [brandColor, venueColor]
  )
  return (
    <Root theme="gx" themeOverride={themeOverride} messages={messages} locale={venueSensitiveLocale}>
      {children}
    </Root>
  )
}

function RecaptchaWrappedReservations() {
  const { invisibleRecaptchaSiteKey, invisibleRecaptchaEnabled } = useVenue()
  configureRecaptcha({ enabled: invisibleRecaptchaEnabled, useRecaptchaNet: true })
  const invisibleRecaptchaRef = useRef<ReCAPTCHARef>(null)
  return (
    <>
      {invisibleRecaptchaEnabled && <ReCaptcha ref={invisibleRecaptchaRef} siteKey={invisibleRecaptchaSiteKey} size="invisible" />}
      <ReCaptchaContext.Provider value={invisibleRecaptchaRef}>
        <Reservations />
      </ReCaptchaContext.Provider>
    </>
  )
}

export function App() {
  return (
    <Provider store={store}>
      <Router>
        <VenueDataLoader>
          <AppMeta />
          <ThirdPartyLoginInitializer />
          <AppRoot>
            <Switch>
              <Route path={routes.reservations.path}>
                <RecaptchaWrappedReservations />
              </Route>
            </Switch>
          </AppRoot>
        </VenueDataLoader>
      </Router>
    </Provider>
  )
}
